'use client';

import React, { useEffect, useState } from 'react';
import { createClient } from '@/lib/supabase/client';

interface Platform {
  id: string;
  name: string;
  category: string;
  active_sessions: number;
  color_class: string;
  dot_color_class: string;
  platform_status: string;
}

function formatSessions(count: number): string {
  if (count >= 1_000_000) return `${(count / 1_000_000).toFixed(1)}M`;
  if (count >= 1_000) return `${(count / 1_000).toFixed(1)}K`;
  return String(count);
}

export default function PlatformGrid() {
  const [platforms, setPlatforms] = useState<Platform[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchPlatforms = async () => {
      const supabase = createClient();
      const { data, error } = await supabase
        .from('platforms')
        .select('id, name, category, active_sessions, color_class, dot_color_class, platform_status')
        .eq('platform_status', 'active')
        .order('active_sessions', { ascending: false });

      if (!error && data) {
        setPlatforms(data);
      }
      setLoading(false);
    };

    fetchPlatforms();
  }, []);

  return (
    <section id="platforms" className="py-20 bg-slate-50" aria-labelledby="platforms-heading">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <span className="inline-block text-xs font-semibold uppercase tracking-widest text-teal-700 mb-3">Platform Coverage</span>
          <h2 id="platforms-heading" className="font-display text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
            Verification across{' '}
            <span className="text-teal-700">every platform your kids love</span>
          </h2>
          <p className="text-slate-600 max-w-xl mx-auto text-base">
            SafePlay works with 40+ gaming platforms. New integrations added monthly — requested by parents like you.
          </p>
        </div>

        <div
          className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3"
          role="list"
          aria-label="Supported gaming platforms"
          aria-busy={loading}
        >
          {loading
            ? Array.from({ length: 12 }).map((_, i) => (
                <div
                  key={i}
                  className="border border-slate-200 rounded-xl p-4 text-center bg-white animate-pulse"
                  aria-hidden="true"
                >
                  <div className="w-10 h-10 rounded-xl bg-slate-200 mx-auto mb-3" />
                  <div className="h-3 bg-slate-200 rounded w-3/4 mx-auto mb-2" />
                  <div className="h-2 bg-slate-100 rounded w-1/2 mx-auto mb-2" />
                  <div className="h-2 bg-slate-100 rounded w-2/3 mx-auto" />
                </div>
              ))
            : platforms?.map((platform) => (
                <div
                  key={platform?.id}
                  role="listitem"
                  className={`border rounded-xl p-4 text-center hover:shadow-md transition-all duration-200 hover:-translate-y-0.5 cursor-default ${platform?.color_class}`}
                  aria-label={`${platform?.name} — ${platform?.category} — ${formatSessions(platform?.active_sessions ?? 0)} active sessions`}
                >
                  <div className={`w-10 h-10 rounded-xl ${platform?.dot_color_class} mx-auto mb-3 flex items-center justify-center shadow-sm`} aria-hidden="true">
                    <span className="text-white text-xs font-bold">{platform?.name?.charAt(0)}</span>
                  </div>
                  <p className="text-sm font-semibold text-slate-800 leading-tight">{platform?.name}</p>
                  <p className="text-xs text-slate-600 mt-0.5">{platform?.category}</p>
                  <div className="mt-2 flex items-center justify-center gap-1" aria-hidden="true">
                    <div className={`w-1.5 h-1.5 rounded-full ${platform?.dot_color_class}`} />
                    <span className="text-xs text-slate-600">{formatSessions(platform?.active_sessions ?? 0)}</span>
                  </div>
                </div>
              ))}

          {/* More platforms card */}
          {!loading && (
            <div
              role="listitem"
              className="border border-dashed border-slate-300 rounded-xl p-4 text-center bg-white flex flex-col items-center justify-center"
              aria-label="28 more platforms available"
            >
              <div className="w-10 h-10 rounded-xl bg-slate-100 mx-auto mb-3 flex items-center justify-center" aria-hidden="true">
                <span className="text-slate-400 text-lg font-bold">+</span>
              </div>
              <p className="text-sm font-semibold text-slate-500">28 more</p>
              <p className="text-xs text-slate-500 mt-0.5">platforms</p>
            </div>
          )}
        </div>

        <p className="text-center text-sm text-slate-600 mt-6">
          Don&apos;t see your platform?{' '}
          <a href="#" className="text-teal-700 font-medium hover:underline focus:outline-none focus:ring-2 focus:ring-teal-500 rounded">Request it →</a>
        </p>
      </div>
    </section>
  );
}